import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import SEO from "../lib/seo";
import PageHero from "../components/PageHero";
import DestinationCard from "../components/DestinationCard";
import CtaSection from "../components/CtaSection";
import SectionHeading from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { getDestinations } from "../lib/api";
import { track } from "../lib/track";

const HERO_IMG =
  "https://images.unsplash.com/photo-1787948258473-0d6ab9a58360?crop=entropy&cs=srgb&fm=jpg&q=80&auto=format&fit=crop&w=1800";

export default function Destinations() {
  const [destinations, setDestinations] = useState([]);
  const [region, setRegion] = useState("Semua");
  const [q, setQ] = useState("");

  useEffect(() => {
    getDestinations().then(setDestinations).catch(() => {});
  }, []);

  const regions = useMemo(
    () => ["Semua", ...Array.from(new Set(destinations.map((d) => d.region)))],
    [destinations]
  );

  const filtered = destinations.filter(
    (d) =>
      (region === "Semua" || d.region === region) &&
      d.name.toLowerCase().includes(q.toLowerCase())
  );

  useEffect(() => {
    track("view_destination", { destination: "catalog" });
  }, []);

  return (
    <>
      <SEO
        title="Destinasi Land Arrangement Internasional | Beyond Travel"
        description="Katalog destinasi Land Arrangement: Malaysia, Singapore, Thailand, China, Egypt, Dubai, Turkey, Japan, Vietnam, Europe, dan America. Custom sesuai kebutuhan Anda."
      />
      <PageHero
        image={HERO_IMG}
        eyebrow="Destinations"
        title="Explore Our Destinations"
        sub="Pilihan destinasi internasional dengan layanan perjalanan yang dapat disesuaikan dengan kebutuhan Anda."
        crumb="Destinations"
      />

      <section className="section bg-cream">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div className="flex flex-wrap gap-2" data-testid="destinations-filter-region">
              {regions.map((r) => (
                <button
                  key={r}
                  onClick={() => {
                    setRegion(r);
                    track("find_trip", { destination: r });
                  }}
                  data-testid={`filter-region-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`chip px-4 py-2 transition-all duration-300 ${
                    region === r
                      ? "bg-tosca-600 text-white shadow-[0_8px_20px_-8px_rgba(15,118,110,0.6)]"
                      : "bg-white text-slate-500 hover:text-tosca-700 shadow-crystal"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="relative md:w-72">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className="field !pl-11"
                placeholder="Cari negara..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="destinations-search-input"
              />
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
              {filtered.map((d, i) => (
                <DestinationCard key={d.slug} destination={d} index={i} />
              ))}
            </div>
          ) : (
            <div className="mt-10 rounded-3xl bg-white p-10 text-center shadow-crystal" data-testid="destinations-empty">
              <p className="font-heading text-xl font-bold text-charcoal">Tidak menemukan destinasi yang sesuai?</p>
              <p className="mt-2 text-sm text-slate-500">Coba kata kunci lain, atau minta tim kami menyusun custom trip untuk Anda.</p>
            </div>
          )}
        </div>
      </section>

      <CtaSection title="Destinasi Impian Anda Belum Terdaftar?" sub="Kami melayani custom trip ke destinasi lain sesuai permintaan. Ceritakan rencana Anda." />
    </>
  );
}
